<script setup lang="ts">
import IconShareStroke from '~/assets/icons/event/share-stroke.svg?component'
import IconShareFill from '~/assets/icons/event/share-fill.svg?component'
import WishlistIcon from '~/assets/icons/wishlist.svg?component'
import type { EventDescription } from '~/composables/types/event'
import { parseEventDescriptionToSearchEngineEventItem } from '~/utils/helpers'

const {
  event,
  isShareButtonVisible = true,
  isGiftcard,
} = defineProps<{
  event: EventDescription
  isShareButtonVisible?: boolean
  isGiftcard?: boolean
}>()

const isShareModalOpen = ref(false)
const { isMobile } = useDevice()
const route = useRoute()
const { t } = useI18n()
const fullLocalizedUrl = useFullLocalizedUrl(true)
const {
  isSearchEngineEventItemOnWishlist,
  addSearchEngineEventItem,
  removeSearchEngineEventItem,
} = useWishlist()

const resolvedIsGiftcard = computed(
  () => isGiftcard ?? eventGetters.isGiftcardEvent(event)
)

const toggleShareModalOpen = () => {
  isShareModalOpen.value = !isShareModalOpen.value
}

const handleOpenShare = () => {
  if (isMobile && navigator.share) {
    sendGalleryShareIntent(event, 'native')
    navigator
      .share({
        title: event.title,
        text: resolvedIsGiftcard.value
          ? t('product.gallery.shareModal.shareGiftcardMessage')
          : t('product.gallery.shareModal.shareEventMessage'),
        url: `${route.path}?utm_source=konfetti&utm_medium=share_event&utm_campaign=mobile`,
      })
      .then(() => {
        sendGalleryShareNativeStatus(event, 'success')
      })
      .catch(() => {
        sendGalleryShareNativeStatus(event, 'cancelled')
      })
  } else {
    toggleShareModalOpen()
    sendGalleryShareIntent(event, 'browser')
  }
}

const shareLink = computed(() =>
  fullLocalizedUrl(
    `${route.path}?${encodeURIComponent(
      'utm_source=konfetti&utm_medium=share_event&utm_campaign=facebook'
    )}`
  )
)

const algoliaEventItem = computed(
  () => parseEventDescriptionToSearchEngineEventItem(event) as any
)

const isOnWishlist = computed(() =>
  isSearchEngineEventItemOnWishlist(algoliaEventItem.value)
)

const handleWishlistToggle = () => {
  if (isOnWishlist.value) {
    removeSearchEngineEventItem(algoliaEventItem.value)
    return
  }

  addSearchEngineEventItem(algoliaEventItem.value)
}
</script>

<template>
  <div class="flex gap-1 md:gap-2 text-xs md:text-base">
    <Button
      variant="text"
      aria-label="Wishlist"
      data-test-id="wishlist-button"
      class="flex items-center gap-1 px-2 py-0.5 md:px-2 relative group no-underline font-normal wishlist-button"
      :class="{ 'wishlist-button--active': isOnWishlist }"
      @click.stop.prevent="handleWishlistToggle"
    >
      <WishlistIcon class="w-5 h-5 text-stone-700" />
      <span class="hidden md:inline-block text-sm text-stone-800">
        {{ t('wishlist.productCard.addToWishlistLabel') }}
      </span>
    </Button>
    <Button
      v-if="isShareButtonVisible"
      variant="text"
      aria-label="Share"
      data-test-id="share-button"
      class="flex items-center gap-1 px-2 py-0.5 md:px-2 relative group no-underline font-normal"
      @click.stop.prevent="handleOpenShare"
    >
      <IconShareFill
        class="w-4 h-4 text-teal-700 transition-opacity duration-200 opacity-0 group-hover:opacity-100"
      />
      <IconShareStroke
        class="w-4 h-4 text-stone-700 absolute transition-opacity duration-200 group-hover:opacity-0"
      />
      <span class="hidden md:inline-block text-sm text-stone-800">
        {{ t('product.shareButton') }}
      </span>
    </Button>
    <ShareDialog
      :is-share-modal-open="isShareModalOpen"
      :is-giftcard="resolvedIsGiftcard"
      :page-title="eventGetters.getTitle(event)"
      :link="shareLink"
      @shared="sendGalleryShareOption(event, $event)"
      @close="toggleShareModalOpen"
    />
  </div>
</template>

<style lang="postcss" scoped>
@keyframes heartbeat {
  0%,
  40%,
  80%,
  100% {
    transform: scale(1);
  }
  20%,
  60% {
    transform: scale(1.25);
  }
}

.wishlist-button {
  :deep(path) {
    @apply stroke-stone-700 duration-300 ease-in-out;
    fill: transparent;
  }

  &:hover :deep(path) {
    @apply stroke-red-500;
  }

  &--active {
    :deep(svg) {
      animation: heartbeat 1s forwards;
      animation-timing-function: ease-in-out;
    }

    :deep(path) {
      @apply fill-red-500 stroke-red-500;
    }
  }

  :deep(svg) {
    filter: none;
  }
}
</style>
